import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Shield, HardDrive, Rocket, ExternalLink, Loader2, FolderOpen } from 'lucide-react';

export default function Profile() {
  const [user, setUser] = useState<any>(null);
  const [usage, setUsage] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadProfile(); }, []);

  async function loadProfile() {
    try {
      const data = await api.getAccount();
      setUser(data.user);
      setUsage(data.usage);
    } catch (e: any) { toast.error(e.message); } finally { setLoading(false); }
  }

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-accent" size={24} /></div>;
  if (!user) return null;

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto">
      <Link to="/dashboard" className="flex items-center gap-2 text-text-muted hover:text-text-primary mb-6">
        <ArrowLeft size={16} /> Back to dashboard
      </Link>

      <h1 className="text-xl font-bold mb-6">Profile</h1>

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-accent/10 border-2 border-accent/30 flex items-center justify-center text-accent text-2xl font-bold shrink-0">
            {user.username?.[0]?.toUpperCase() || '?'}
          </div>
          <div>
            <h2 className="text-lg font-bold">{user.username}</h2>
            <p className="text-text-muted text-sm flex items-center gap-1"><Mail size={12} /> {user.email}</p>
            <span className="badge badge-success text-[10px] mt-1 capitalize">{user.tier}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { icon: FolderOpen, color: 'text-accent', val: usage?.projects || 0, label: 'Projects' },
          { icon: HardDrive, color: 'text-accent-blue', val: formatBytes(usage?.storage_bytes || 0), label: 'Storage' },
          { icon: Rocket, color: 'text-accent-purple', val: usage?.deploys || 0, label: 'Deploys' },
          { icon: Shield, color: 'text-warning', val: (user.mgc_balance || 0).toLocaleString(), label: 'MGC Balance' },
        ].map(s => (
          <div key={s.label} className="card py-3 px-4 text-center">
            <s.icon size={18} className={`mx-auto mb-1 ${s.color}`} />
            <p className="text-xl font-bold">{s.val}</p>
            <p className="text-[10px] text-text-muted">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="card mb-6">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2"><ExternalLink size={14} className="text-accent" /> Megan Services</h3>
        <p className="text-xs text-text-muted mb-3">Live APIs and services for your projects. No API key required for most endpoints.</p>
        <div className="space-y-2">
          {[
            { name: 'Megan APIs', url: 'https://apis.megan.qzz.io', desc: '585+ endpoints — anime, movies, sports, downloads, AI' },
            { name: 'Megan Chat', url: 'https://chat.megan.qzz.io', desc: 'Real-time chat service for your apps' },
            { name: 'Megan AI', url: 'https://ai.megan.qzz.io', desc: 'AI-powered assistants and completions' },
            { name: 'Megan Auth', url: 'https://auth.megan.qzz.io', desc: 'Single sign-on, API keys, session management' },
          ].map(svc => (
            <a key={svc.url} href={svc.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-elevated transition-colors border border-transparent hover:border-border">
              <div><p className="text-sm font-medium">{svc.name}</p><p className="text-[11px] text-text-muted">{svc.desc}</p></div>
              <ExternalLink size={12} className="text-text-muted shrink-0" />
            </a>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 className="font-semibold text-sm mb-3">Contact & Support</h3>
        <div className="space-y-2 text-xs text-text-muted">
          <p>📧 <a href="mailto:trackerwanga@gmail.com" className="text-accent hover:underline">trackerwanga@gmail.com</a></p>
          <p>📱 WhatsApp: <a href="https://wa.me/254769502217" className="text-accent hover:underline" target="_blank" rel="noopener noreferrer">+254 769 502 217</a></p>
          <p>📞 Call: <a href="tel:+254758476795" className="text-accent hover:underline">+254 758 476 795</a></p>
        </div>
      </div>
    </div>
  );
}

function formatBytes(b: number): string {
  if (!b) return '0 B';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}
